import {QueryClient, queryOptions, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {useState} from "react";

import {useToast} from "@/hooks/use-toast";
import * as apiClient from '@/lib/api'
import {ApiError} from "@/lib/api";
import {ArticleDetail, ArticleFormValues, SubmitForReviewArticle, Tag} from "@/modules/articles/article-editor/types";
import {canSubmitForReview, getMissingFieldsForReview, hasUrlInBody, stripHtmlTags} from "@/modules/articles/article-editor/utils";
import {QUERY_KEY_ARTICLE, QUERY_KEY_ARTICLE_TAGS, QUERY_KEY_ARTICLES} from "@/utils/keys";
import {ARTICLE_TAGS_API_URL, ARTICLES_API_URL} from "@/utils/urls";

const articleQueryOptions = (articleId: number) =>
  queryOptions({
    queryKey: [QUERY_KEY_ARTICLE, articleId],
    queryFn: (): Promise<ArticleDetail> => apiClient.get(`${ARTICLES_API_URL}/${articleId}`),
  });

export const useArticle = (articleId: number) => useQuery(articleQueryOptions(articleId))

export const useArticleTags = () => useQuery({
  queryKey: [QUERY_KEY_ARTICLE_TAGS],
  queryFn: (): Promise<Tag[]> => apiClient.get(ARTICLE_TAGS_API_URL),
})

export function createArticle(): Promise<ArticleDetail> {
  return apiClient.post(ARTICLES_API_URL, {title: '', summary: '', body: '', tagIds: []})
}

export function updateArticle({id, ...values}: { id: number } & ArticleFormValues): Promise<ArticleDetail> {
  return apiClient.put(`${ARTICLES_API_URL}/${id}`, values)
}

export function publishArticle(id: number): Promise<ArticleDetail> {
  return apiClient.post(`${ARTICLES_API_URL}/${id}/publish`, {standardsConfirmed: true})
}

export function unpublishArticle(id: number): Promise<ArticleDetail> {
  return apiClient.post(`${ARTICLES_API_URL}/${id}/unpublish`, {})
}

export function publishArticleToAdvisers(id: number): Promise<ArticleDetail> {
  return apiClient.post(`${ARTICLES_API_URL}/${id}/publish-to-advisers`, {})
}

export function unpublishArticleToAdvisers(id: number): Promise<ArticleDetail> {
  return apiClient.post(`${ARTICLES_API_URL}/${id}/unpublish-to-advisers`, {})
}

export function deleteArticle(id: number): Promise<void> {
  return apiClient.remove(`${ARTICLES_API_URL}/${id}`)
}

function invalidateArticle(queryClient: QueryClient, articleId: number) {
  queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLES]})
  queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLE, articleId]})
}

export const useUpdateArticleMutation = (articleId: number) => {
  const queryClient = useQueryClient()
  const {toast} = useToast()

  return useMutation({
    mutationFn: updateArticle,
    onSuccess: () => {
      invalidateArticle(queryClient, articleId)
      toast({description: 'Article saved.', variant: 'mint'})
    },
    onError: (e) => {
      toast({description: e instanceof ApiError && e.message ? e.message : 'Error saving article.', variant: 'red'})
    },
  })
}

// Used for auto-save while the user is editing - no toast, since one would fire on every
// debounced save and spam the user while they're still actively working on the article.
// Only the single article is invalidated (not the articles list) since nothing list-relevant
// changes on a title/body/tag edit, and the list would otherwise get refetched on every
// debounced save while the user is actively typing.
export const useAutoSaveArticleMutation = (articleId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateArticle,
    onSuccess: () => queryClient.invalidateQueries({queryKey: [QUERY_KEY_ARTICLE, articleId]}),
  })
}

export const usePublishArticleMutation = (articleId: number) => {
  const queryClient = useQueryClient()
  const {toast} = useToast()

  return useMutation({
    mutationFn: () => publishArticle(articleId),
    onSuccess: () => {
      invalidateArticle(queryClient, articleId)
      toast({description: 'Article submitted for review.', variant: 'mint'})
    },
    onError: (e) => {
      toast({description: e instanceof ApiError && e.message ? e.message : 'Error submitting article.', variant: 'red'})
    },
  })
}

export const useSubmitForReview = (article: SubmitForReviewArticle, options?: { onSuccess?: () => void }) => {
  const [open, setOpen] = useState(false)
  const [standardsConfirmed, setStandardsConfirmed] = useState(false)
  const mutation = usePublishArticleMutation(article.id)

  const bodyPlainText = stripHtmlTags(article.body ?? '')
  const missingForReview = getMissingFieldsForReview(article.title, article.summary, bodyPlainText)
  const urlInBody = hasUrlInBody(bodyPlainText)
  const canSubmit = canSubmitForReview(article.status, missingForReview.length, bodyPlainText)
  const isInReview = article.status === 'IN_REVIEW'

  const openDialog = () => {
    setStandardsConfirmed(false)
    setOpen(true)
  }

  const handleConfirm = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        setOpen(false)
        setStandardsConfirmed(false)
        options?.onSuccess?.()
      },
    })
  }

  return {
    canSubmit,
    isInReview,
    missingForReview,
    urlInBody,
    openDialog,
    dialogProps: {
      open,
      onOpenChange: setOpen,
      standardsConfirmed,
      onStandardsConfirmedChange: setStandardsConfirmed,
      onConfirm: handleConfirm,
      pending: mutation.isPending,
    },
  }
}

export const useUnpublishArticleMutation = (articleId: number) => {
  const queryClient = useQueryClient()
  const {toast} = useToast()

  return useMutation({
    mutationFn: () => unpublishArticle(articleId),
    onSuccess: () => {
      invalidateArticle(queryClient, articleId)
      toast({description: 'Article unpublished.', variant: 'mint'})
    },
    onError: () => {
      toast({description: 'Error unpublishing article.', variant: 'red'})
    },
  })
}

export const useUnpublish = (articleId: number, options?: { onSuccess?: () => void }) => {
  const [open, setOpen] = useState(false)
  const mutation = useUnpublishArticleMutation(articleId)

  const openDialog = () => {
    setOpen(true)
  }

  const handleConfirm = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        setOpen(false)
        options?.onSuccess?.()
      },
    })
  }

  return {
    openDialog,
    dialogProps: {
      open,
      onOpenChange: setOpen,
      onConfirm: handleConfirm,
      pending: mutation.isPending,
    },
  }
}
